import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    CircularProgress,
    makeStyles,
    Tooltip,
    useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ModalUtils from 'utils/ModalUtils';
import { Scanner } from '@yudiel/react-qr-scanner';
import FilterCenterFocusIcon from '@material-ui/icons/FilterCenterFocus';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';

const useStyles = makeStyles((theme) => ({
    scannerParent: {
        '@media (orientation: landscape)': {
            position: 'fixed',
            width: '100%',
            top: 0,
            left: 0,
            zIndex: 9999,
        },
        '@media (orientation: portrait)': {
            position: 'fixed',
            left: '10px',
            top: '10px',
            height: '100%',
            width: '95%',
            marginRight: '10px',
            zIndex: 9999,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.outerSpace,
        },
    },
    scannerParentDesktop: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '700px',
        zIndex: 9999,
    },
    closeButton: {
        opacity: 0,
        animation: 'fadeIn 0.5s ease-in forwards',
        animationDelay: '1s',
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 99999,
        cursor: 'pointer',
        backgroundColor: theme.palette.background.white,
        '& > svg': {
            fill: theme.palette.background.red,
            width: '34px',
            height: '34px',
        },
    },
}));

/**
 * Parse AAMVA PDF417 barcode data from driver's license
 * @param {string} data - Raw barcode data
 * @returns {object} Parsed customer data
 */
const parseLicenseData = (data) => {
    const result = {
        firstName: '',
        middleName: '',
        lastName: '',
        dob: null,
        dln: '',
        address1: '',
        city: '',
        state: '',
        zipCode: '',
    };

    if (!data) return result;

    // Parse AAMVA data by extracting field-value pairs
    const cleanData = data
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\|n/gi, '\n')
        .replace(/\\n/g, '\n');

    // List of valid AAMVA codes to use as delimiters
    const validCodes = [
        'DAA', 'DAB', 'DAC', 'DAD', 'DAE', 'DAF', 'DAG', 'DAH', 'DAI', 'DAJ',
        'DAK', 'DAL', 'DAM', 'DAN', 'DAO', 'DAP', 'DAQ', 'DAR', 'DAS', 'DAT',
        'DAU', 'DAV', 'DAW', 'DAX', 'DAY', 'DAZ', 'DBA', 'DBB', 'DBC', 'DBD',
        'DBE', 'DBF', 'DBG', 'DBH', 'DBI', 'DBJ', 'DBK', 'DBL', 'DBM', 'DBN',
        'DBO', 'DBP', 'DBQ', 'DBR', 'DBS', 'DCA', 'DCB', 'DCC', 'DCD', 'DCE',
        'DCF', 'DCG', 'DCH', 'DCI', 'DCJ', 'DCK', 'DCL', 'DCM', 'DCN', 'DCO',
        'DCP', 'DCQ', 'DCR', 'DCS', 'DCT', 'DCU', 'DDA', 'DDB', 'DDC', 'DDD',
        'DDE', 'DDF', 'DDG', 'DDH', 'DDI', 'DDJ', 'DDK', 'DDL', 'DDM', 'DDN',
        'DDO', 'DDP', 'DDQ', 'DDR', 'PAA', 'PAB', 'PAC', 'PAD', 'PAE', 'PAF',
        'ZPA', 'ZPB', 'ZPC', 'ZPD', 'ZPE', 'ZPF',
    ];

    // Extract specific field by searching for code followed by value
    const extractField = (code) => {
        const otherCodes = validCodes.filter((c) => c !== code).join('|');
        const codeIndex = cleanData.indexOf(code);
        if (codeIndex === -1) return '';

        const startPos = codeIndex + code.length;
        const remainingText = cleanData.substring(startPos);

        // Find next code that appears at start of a line (after newline)
        // This prevents matching codes inside words like "DDL" in "MIDDLENAME"
        const nextCodeRegex = new RegExp(`\\n(${otherCodes})(?![A-Za-z0-9])`, 'i');
        const nextCodeMatch = remainingText.match(nextCodeRegex);
        const newlineIndex = remainingText.indexOf('\n');

        let endPos = remainingText.length;
        if (nextCodeMatch && nextCodeMatch.index !== undefined) {
            // Found next code at line start
            endPos = nextCodeMatch.index;
        } else if (newlineIndex !== -1) {
            // Use newline as delimiter
            endPos = newlineIndex;
        }

        return remainingText.substring(0, endPos).trim();
    };
    // Helper to get field with fallback codes
    const getField = (codes) => {
        let foundValue = '';
        codes.some((code) => {
            const value = extractField(code);
            if (value) {
                foundValue = value;
                return true;
            }
            return false;
        });
        return foundValue;
    };

    result.lastName = getField(['DCS', 'DAB']);

    const firstName = getField(['DAC', 'DCT']);
    if (firstName) {
        const nameParts = firstName.split(/[,\s]+/);
        result.firstName = nameParts[0] || '';
        if (nameParts.length > 1) {
            result.middleName = nameParts.slice(1).join(' ');
        }
    }

    if (!result.lastName && !result.firstName) {
        const fullName = getField(['DAA']);
        if (fullName) {
            const nameParts = fullName.split(',').map((p) => p.trim());
            if (nameParts.length >= 2) {
                const [lastName, firstAndMiddle] = nameParts;
                result.lastName = lastName;
                const [first, ...middle] = firstAndMiddle.split(/\s+/);
                result.firstName = first || '';
                result.middleName = middle.join(' ');
            }
        }
    }

    if (!result.middleName) {
        result.middleName = getField(['DAD']);
    }

    const dobRaw = getField(['DBB']).replace(/[^0-9]/g, '');
    if (dobRaw.length === 8) {
        const isMMDDYYYY = parseInt(dobRaw.substring(0, 2), 10) <= 12;
        const [m, d, y] = isMMDDYYYY
            ? [dobRaw.substring(0, 2), dobRaw.substring(2, 4), dobRaw.substring(4, 8)]
            : [dobRaw.substring(4, 6), dobRaw.substring(6, 8), dobRaw.substring(0, 4)];

        const month = parseInt(m, 10);
        const day = parseInt(d, 10);
        const year = parseInt(y, 10);

        if (year > 1900 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            result.dob = new Date(year, month - 1, day);
        }
    }

    result.dln = getField(['DAQ']);
    result.address1 = getField(['DAG']);
    result.city = getField(['DAI']);
    result.state = getField(['DAJ']);

    const zipRaw = getField(['DAK']);
    if (zipRaw) {
        const zipClean = zipRaw.replace(/[^0-9]/g, '');
        result.zipCode = zipClean.substring(0, 5);
    }

    return result;
};

const LicenseScanner = ({ onScanComplete, disabled, loading }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = useStyles();
    const [showScanner, setShowScanner] = useState(false);
    const contentScanButton = (loading) ? <CircularProgress color="inherit" size={20} /> : <FilterCenterFocusIcon />;

    const generalErrorMessage = 'Error scanning license';

    const checkCameraAvailability = async () => {
        const errorMessage = 'You do not have a camera on this device';

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoInputs = devices.filter((device) => device.kind === 'videoinput');
            if (videoInputs.length === 0) {
                ModalUtils.errorMessage(null, errorMessage);
                return;
            }

            setShowScanner(true);
        } catch (error) {
            ModalUtils.errorMessage(null, errorMessage);
        }
    };

    const onReadCode = (input) => {
        if (input?.length === 0) {
            ModalUtils.errorMessage(null, generalErrorMessage);
            setShowScanner(false);
            return;
        }

        const code = input[0].rawValue ?? '';
        if (!code) {
            ModalUtils.errorMessage(null, generalErrorMessage);
            setShowScanner(false);
            return;
        }

        const parsedData = parseLicenseData(code);
        if (!parsedData.firstName && !parsedData.lastName && !parsedData.dln) {
            ModalUtils.errorMessage(null, 'Could not parse license data');
            setShowScanner(false);
            return;
        }

        onScanComplete(parsedData);
        setShowScanner(false);
        ModalUtils.successMessage([{ message: 'License scanned successfully' }]);
    };

    return (
        <>
            <Tooltip title="Scan Driver's License" placement="top">
                <Button
                    variant="outlined"
                    disabled={disabled || loading}
                    onClick={() => checkCameraAvailability()}
                >
                    { contentScanButton }
                </Button>
            </Tooltip>
            {showScanner && (
                <div className={isMobile ? classes.scannerParent : classes.scannerParentDesktop}>
                    <div
                        onClick={() => setShowScanner(false)}
                        className={classes.closeButton}
                    >
                        <HighlightOffOutlinedIcon />
                    </div>
                    <Scanner
                        onScan={(detectedCodes) => onReadCode(detectedCodes)}
                        onError={(error) => ModalUtils.errorMessage(null, error.message)}
                        components={{
                            audio: true,
                            onOff: false,
                            torch: false,
                            zoom: false,
                            finder: false,
                        }}
                        scanDelay={1000}
                        constraints={{
                            facingMode: 'environment',
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                        }}
                        formats={['pdf417']}
                    />
                </div>
            )}
        </>
    );
};

LicenseScanner.propTypes = {
    onScanComplete: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
};

LicenseScanner.defaultProps = {
    disabled: false,
    loading: false,
};

export default LicenseScanner;
